import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  TableChart as CsvIcon,
  Description as XlsxIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    minWidth: 320,
    maxWidth: 400,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.common.white,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.grey[700],
    transform: 'translateY(-2px)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.common.white,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (format: 'csv' | 'xlsx' | 'pdf' | 'email') => void;
  loading?: boolean;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  open,
  onClose,
  onExport,
  loading = false
}) => {
  const exportOptions = [
    {
      format: 'csv' as const,
      title: 'CSV',
      description: 'Download as comma-separated values',
      icon: CsvIcon,
      color: '#4caf50'
    },
    {
      format: 'xlsx' as const,
      title: 'Excel',
      description: 'Download as Excel spreadsheet',
      icon: XlsxIcon,
      color: '#f97316'
    },
    {
      format: 'pdf' as const,
      title: 'PDF',
      description: 'Download as PDF document',
      icon: PdfIcon,
      color: '#f44336'
    },
    {
      format: 'email' as const,
      title: 'Email',
      description: 'Share via email',
      icon: EmailIcon,
      color: '#ff9800'
    }
  ];

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DownloadIcon />
          <Typography variant="h6">Export Data</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      
      <DialogContent sx={{ px: 2, py: 1 }}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {exportOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <Grid item xs={12} key={option.format}>
                <StyledCard
                  onClick={() => onExport(option.format)}
                  sx={{ 
                    opacity: loading ? 0.6 : 1,
                    minHeight: 'auto'
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1.5,
                    '&:last-child': { pb: 1.5 }
                  }}>
                    <IconComponent 
                      sx={{ 
                        fontSize: 28, 
                        color: option.color,
                        flexShrink: 0
                      }} 
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="grey.400" sx={{ fontSize: '0.75rem' }}>
                        {option.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button 
          onClick={onClose}
          color="inherit"
          disabled={loading}
          size="small"
        >
          Cancel
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};